import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { environment } from "../../../environments/environment";
import { GenericResponseDto } from "../models/generic-response.dto";
import { ApiCacheService } from "./api-cache.service";
import { AssignOrderToProjectDto } from "../models/orders/assignOrderToProjectDto";
import { CopyLinesDto } from "../models/orders/copyLinesDto";
import { CreateSalesOrderLineResponseDto } from "../models/orders/createSalesOrderLineResponseDto";
import { DeleteSOLinesResponseDto } from "../models/orders/deleteSOLinesResponseDto";
import { FreightChargesListDto } from "../models/orders/freightChargesListDto";
import { GetExtisingPOIdRequest } from "../models/orders/getExtisingPOIdRequest";
import { GetExtisingPOIdResponse } from "../models/orders/getExtisingPOIdResponse";
import { GetProjectInvoiceReportRequestDto } from "../models/orders/getProjectInvoiceReportRequestDto";
import { GetProjectInvoiceReportResponseDto } from "../models/orders/getProjectInvoiceReportResponseDto";
import { InvoiceEmail } from "../models/orders/invoicedEmail";
import { OrderBcEmailDto } from "../models/orders/orderBcEmailDto";
import { PreSalesProjectWorkflowList } from "../models/orders/preSalesProjectWorkflowList";
import { PrintGetSalesOrderReportHeaderRequest } from "../models/orders/PrintGetSalesOrderReportHeaderRequest";
import { PrintGetSalesOrderReportHeaderResponse } from "../models/orders/PrintGetSalesOrderReportHeaderResponse";
import { PrintPurchaseOrderDto } from "../models/orders/purchaseOrderLine";
import { SaleOrderLineAddressDto } from "../models/orders/saleOrderLineAddressDto";
import { SaleOrderResponseDto } from "../models/orders/saleOrderResponseDto";
import { SalesOrderCommissionBC } from "../models/orders/salesOrderCommissionBc";
import { UpdateSalesOrderHeaderDto } from "../models/orders/updateSalesOrderHeaderDto";
import { UpdateTrackableOnSalesOrderLinesRequestDto } from "../models/orders/updateTrackableOnSalesOrderLinesRequestDto";
import { VendorInvoiceJournalDetailValue } from "../models/orders/vendorInvoiceJournalDetailValue";
import { Employee } from "../models/profile/employee";
import { CreateSalesOrderEmailResponseDto } from "../models/invoice-review/createSalesOrderEmailResponseDto";
import { UsersList } from "../models/users/users/usersList";
import { UpdateSalesOrderHeaderResponseDto } from "../models/orders/updateSalesOrderHeaderResponseDto";
import { Client } from "../models/client/details/client";
import { ReleasedProduct } from "../models/shared/releaseProduct";
import { ProjectSummaryRequestDto } from "../models/project-workflow/projectSummaryRequestDto";

@Injectable({
  providedIn: "root",
})
export class OrderSummaryService {
  private clientManagementBaseURL = environment.baseUrl + "/ClientManagement";
  //private clientManagementBaseURL = environment.baseUrl + '/ClientManagement';
  private saleOrderManagementBaseURL =
    environment.baseUrl + "/SalesOrderManagement";
  private userManagementBaseURL = environment.baseUrl + "/User";
  private d365SynchroniserBaseURL = environment.baseUrl + "/D365Synchroniser";
  private invoicePreviewBaseURL = environment.baseUrl + "/InvoicePreview";
  private samplepoManagementBaseURL =
    environment.baseUrl + "/SamplePOMangement";
  private productionWorkflowBaseURL = environment.baseUrl + "/ProjectWorkflow";
  private readonly getAllReleaseProductUrl =
    this.samplepoManagementBaseURL + "/GetAllReleaseProduct";
  private readonly releaseProductTtl = 8 * 60 * 60 * 1000; // 8 hours

  constructor(
    private _http: HttpClient,
    private _apiCache: ApiCacheService
  ) {}

  getSaleOrderDataAgainstSaleId(
    SaleOrderId: string
  ): Observable<GenericResponseDto<Client[]>> {
    return this._http.get<GenericResponseDto<Client[]>>(
      `${this.clientManagementBaseURL}/GetAllContactsByAccountNumber?AccountNumber=${SaleOrderId}`
    );
  }

  getSaleOrderHeaderData(
    id: string
  ): Observable<GenericResponseDto<SaleOrderResponseDto>> {
    return this._http.get<GenericResponseDto<SaleOrderResponseDto>>(
      this.saleOrderManagementBaseURL + "/GetSalesOrderHeader?SaleOrderId=" + id
    );
  }

  getSalesOrderInvoiceEmails(
    salesOrderId: string
  ): Observable<GenericResponseDto<InvoiceEmail[]>> {
    return this._http.get<GenericResponseDto<InvoiceEmail[]>>(
      this.saleOrderManagementBaseURL +
        "/GetSalesOrderInvoiceEmails?SaleOrderId=" +
        salesOrderId
    );
  }

  getSaleOrderLinesData(
    id: string
  ): Observable<GenericResponseDto<SaleOrderLineAddressDto[]>> {
    return this._http.get<GenericResponseDto<SaleOrderLineAddressDto[]>>(
      this.saleOrderManagementBaseURL + "/GetSaleOrderLines?SaleOrderId=" + id
    );
  }

  GetJNIDeletedRecordsFilter(
    odataFilter: string
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.d365SynchroniserBaseURL +
        "/GetJNIDeletedRecordsFilter?odataFilter=" +
        odataFilter
    );
  }

  getBACList(id: number): Observable<GenericResponseDto<UsersList[]>> {
    return this._http.get<GenericResponseDto<UsersList[]>>(
      this.userManagementBaseURL + "/GetAllWhoSupportsMeByUserId?userid=" + id
    );
  }

  updateSaleOrderHeaderData(
    payload: UpdateSalesOrderHeaderDto
  ): Observable<GenericResponseDto<UpdateSalesOrderHeaderResponseDto>> {
    return this._http.post<
      GenericResponseDto<UpdateSalesOrderHeaderResponseDto>
    >(this.saleOrderManagementBaseURL + "/UpdateSalesOrderHeader", payload);
  }

  addLineData(
    payload: CopyLinesDto
  ): Observable<GenericResponseDto<CreateSalesOrderLineResponseDto>> {
    return this._http.post<GenericResponseDto<CreateSalesOrderLineResponseDto>>(
      this.saleOrderManagementBaseURL + "/CopyLines",
      payload
    );
  }

  getRecommendedTax(
    saleOrderId = ""
  ): Observable<GenericResponseDto<number | null>> {
    return this._http.get<GenericResponseDto<number | null>>(
      this.saleOrderManagementBaseURL +
        `/CalculateTax?SaleOrderId=${saleOrderId}`
    );
  }

  getEmployeeByPersonnelNumber(
    personnelNumber = ""
  ): Observable<GenericResponseDto<Employee>> {
    return this._http.get<GenericResponseDto<Employee>>(
      this.userManagementBaseURL +
        `/GetEmployeeByPersonnelNumber?PersonnelNumber=${personnelNumber}`
    );
  }

  IsUserBlockedFromEdit(
    saleOrderId = ""
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.saleOrderManagementBaseURL +
        `/IsUserBlockedFromEdit?SalesOrderId=${saleOrderId}`
    );
  }

  getAllCategory(): Observable<GenericResponseDto<ReleasedProduct[]>> {
    return this._apiCache.get<GenericResponseDto<ReleasedProduct[]>>(
      this.getAllReleaseProductUrl,
      this.releaseProductTtl
    );
  }

  getUnmatchQuantityUrl(
    oDataFilter: string
  ): Observable<GenericResponseDto<VendorInvoiceJournalDetailValue[]>> {
    return this._http.get<
      GenericResponseDto<VendorInvoiceJournalDetailValue[]>
    >(
      this.d365SynchroniserBaseURL +
        `/GetVendorInvoiceJournal?odataFilter=${oDataFilter}`
    );
  }

  printProjectConfirmation(
    saleOrderId: string,
    forced = "true"
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.saleOrderManagementBaseURL +
        `/GetSalesOrderConfirmationJournal?saleOrderId=${saleOrderId}&type=confirmation&forced=${forced}`
    );
  }

  printProjectApproval(
    saleOrderId: string,
    forced = "true"
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.saleOrderManagementBaseURL +
        `/GetSalesOrderConfirmationJournal?saleOrderId=${saleOrderId}&type=approval&forced=${forced}`
    );
  }

  deleteLineItems(
    payload: number[]
  ): Observable<GenericResponseDto<DeleteSOLinesResponseDto>> {
    return this._http.post<GenericResponseDto<DeleteSOLinesResponseDto>>(
      this.saleOrderManagementBaseURL + "/DeleteSalesOrderLine",
      payload
    );
  }

  getProjectInvoiceReport(
    payload: GetProjectInvoiceReportRequestDto
  ): Observable<GenericResponseDto<GetProjectInvoiceReportResponseDto>> {
    return this._http.post<
      GenericResponseDto<GetProjectInvoiceReportResponseDto>
    >(this.d365SynchroniserBaseURL + "/GetProjectInvoiceReport", payload);
  }

  GetExtisingPOId(
    payload: GetExtisingPOIdRequest
  ): Observable<GenericResponseDto<GetExtisingPOIdResponse>> {
    return this._http.post<GenericResponseDto<GetExtisingPOIdResponse>>(
      this.d365SynchroniserBaseURL + "/GetExtisingPOId",
      payload
    );
  }

  getFreightCharges(
    saleOrderId: string
  ): Observable<GenericResponseDto<FreightChargesListDto[]>> {
    return this._http.get<GenericResponseDto<FreightChargesListDto[]>>(
      this.saleOrderManagementBaseURL +
        `/GetFreightChargesDetails?SaleOrderId=${saleOrderId}`
    );
  }

  getFreightChargesData(
    saleOrderId: string
  ): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.saleOrderManagementBaseURL +
        `/GetFreightCharges?SaleOrderId=${saleOrderId}`
    );
  }

  updateInvoiceEmailSaleOrderHeaderData(
    payload: InvoiceEmail[]
  ): Observable<GenericResponseDto<CreateSalesOrderEmailResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreateSalesOrderEmailResponseDto>
    >(this.saleOrderManagementBaseURL + "/AddOrUpdateInvoicedEmails", payload);
  }

  // deleteInvoiceEmailSaleOrderHeaderData(
  //   payload: number[]
  // ): Observable<GenericResponseDto<boolean>> {
  //   return this._http.post<GenericResponseDto<boolean>>(
  //     this.saleOrderManagementBaseURL + '/DeleteInvoicedEmails',
  //     payload
  //   );
  // }

  deleteInvoiceEmailSaleOrderHeaderData(
    payload: number[]
  ): Observable<GenericResponseDto<CreateSalesOrderEmailResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreateSalesOrderEmailResponseDto>
    >(this.saleOrderManagementBaseURL + "/DeleteSalesOrderEmail", payload);
  }

  printPOForSalesOrder(
    purchaseOrderID: string,
    watermarkType = ""
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.samplepoManagementBaseURL +
        `/PrintPOForSalesOrder?PurchaseOrderID=${purchaseOrderID}&WatermarkType=${watermarkType}`
    );
  }

  printAllPOs(saleOrderId: string): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.samplepoManagementBaseURL +
        "/PrintMergedPOForSalesOrder?SalesOrderId=" +
        saleOrderId
    );
  }

  getPurchaseOrderLine(
    SalesOrderId: string
  ): Observable<GenericResponseDto<PrintPurchaseOrderDto[]>> {
    return this._http.get<GenericResponseDto<PrintPurchaseOrderDto[]>>(
      this.saleOrderManagementBaseURL +
        "/GetPurchaseOrderPrintDetails?SaleOrderId=" +
        SalesOrderId
    );
  }

  OrderSummaryDocument(
    salesOrderId: string,
    watermarkType = ""
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.saleOrderManagementBaseURL +
        `/OrderSummaryDocument?salesOrderId=${salesOrderId}&WatermarkType=${watermarkType}`
    );
  }

  /**
   * Generates a Project Summary PDF (header from the backend + supplied workflow line items).
   * @param request The sales-order ID and ordered list of workflow line items to render.
   * @returns Observable of a base64-encoded PDF string.
   */
  ProjectSummaryDocument(
    request: ProjectSummaryRequestDto
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.saleOrderManagementBaseURL + "/ProjectSummaryDocument",
      request
    );
  }

  getSalesOrderCommissionBc(
    salesOrderId: string
  ): Observable<GenericResponseDto<SalesOrderCommissionBC[]>> {
    return this._http.get<GenericResponseDto<SalesOrderCommissionBC[]>>(
      this.saleOrderManagementBaseURL +
        "/GetSalesOrderCommissionBC?SaleOrderId=" +
        salesOrderId
    );
  }

  printGetSalesOrderReportHeader(
    payload: PrintGetSalesOrderReportHeaderRequest
  ): Observable<GenericResponseDto<PrintGetSalesOrderReportHeaderResponse>> {
    return this._http.post<
      GenericResponseDto<PrintGetSalesOrderReportHeaderResponse>
    >(
      this.saleOrderManagementBaseURL + "/PrintGetSalesOrderReportHeader",
      payload
    );
  }

  getPreSalesProjectList(
    clientAccount: string
  ): Observable<GenericResponseDto<PreSalesProjectWorkflowList[]>> {
    return this._http.get<GenericResponseDto<PreSalesProjectWorkflowList[]>>(
      this.productionWorkflowBaseURL +
        "/GetPresaleProjectHeaderList?ClientId=" +
        clientAccount
    );
  }

  getSalesOrderAssignedProject(
    salesOrderId: string
  ): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.productionWorkflowBaseURL +
        "/GetSalesOrderAssignedProject?SaleOrderId=" +
        salesOrderId
    );
  }

  postAssignedProjectToSalesOrder(
    payload: AssignOrderToProjectDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/AddSaleOrderIdProjectHeader",
      payload
    );
  }

  getOrderBacEmailBySalesOrderId(
    salesOrderId: string
  ): Observable<GenericResponseDto<OrderBcEmailDto>> {
    return this._http.get<GenericResponseDto<OrderBcEmailDto>>(
      this.invoicePreviewBaseURL +
        "/GetOrderBacEmailBySalesOrderId?salesOrderId=" +
        salesOrderId
    );
  }

  getOrderBcEmailBySalesOrderId(
    salesOrderId: string
  ): Observable<GenericResponseDto<OrderBcEmailDto>> {
    return this._http.get<GenericResponseDto<OrderBcEmailDto>>(
      this.invoicePreviewBaseURL +
        "/GetOrderBcEmailBySalesOrderId?salesOrderId=" +
        salesOrderId
    );
  }

  updateTrackableOnSalesOrderLines(
    payload: UpdateTrackableOnSalesOrderLinesRequestDto[]
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.saleOrderManagementBaseURL + "/UpdateTrackableOnSalesOrderLines",
      payload
    );
  }
}
